import Route from '@ember/routing/route'
import { service } from '@ember/service'
import { action } from '@ember/object'
import { assert } from '@ember/debug'
import { macroCondition, isDevelopingApp, isTesting, importSync } from '@embroider/macros'
import { isAbortError, isUnauthorizedError, isForbiddenError } from 'district-ui-client/errors/http-error'
import config from 'district-ui-client/config/environment'
import type SessionService from 'district-ui-client/services/session'
import type { IntlService } from 'ember-intl'
import type AuthToken from '@blakeelearning/auth/services/auth-token'
import type { Log } from '@blakeelearning/log'
import type Store from '@ember-data/store'
import type GradeSetsService from 'district-ui-client/services/grade-sets'
import type ActiveRouteService from 'district-ui-client/services/active-route'
import type ReleaseCheckerService from '@blakeelearning/app-refresher/release-checker/service'
import type LongSessionKillerService from '@blakeelearning/app-refresher/long-session-killer/service'
import type SessionTrackerService from '@blakeelearning/app-refresher/session-tracker/service'
import type RefresherService from '@blakeelearning/app-refresher/refresher/service'
import type Transition from '@ember/routing/transition'
import type DistrictCoordinator from 'district-ui-client/models/district-coordinator'
import type District from 'district-ui-client/models/district'
import type { DecodedAuthToken } from 'district-ui-client/services/session'
import type AnalyticsService from 'district-ui-client/services/analytics'
import type RouterService from '@ember/routing/router-service'
import type WindowService from 'district-ui-client/services/window'
import type TeacherEvents from 'district-ui-client/services/teacher-events'
import type { RouteWithKnownAttributes } from 'district-ui-client/services/teacher-events'

/*
 * Produce a list of supported language tags, by looking at what translation json files we have defined
 *
 * Example result:
 * ['en-us', 'en-au', 'en', 'en-ca', 'en-gb']
 *
 * Note that the ordering of the resulting array should not be used for anything.
 */
const imports = import.meta.glob('@translations/*.json') // not eager - don't import them yet.
const filenames = Object.keys(imports)
  .map((importPath) => importPath.split('/').pop())
  .filter((filename) => typeof filename === 'string')
const supportedLanguageTags = filenames.map((filename) => filename.split('.json')[0])

async function bootMirage() {
  // don't start mirage if jupiter is running
  if (config.isJupiter) return

  const startMirage: any = importSync('district-ui-client/mirage/servers/default')
  // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
  await startMirage.default({})
}

export default class ApplicationRoute extends Route {
  @service authToken!: AuthToken
  @service gradeSets!: GradeSetsService
  @service intl!: IntlService
  @service log!: Log
  @service longSessionKiller!: LongSessionKillerService
  @service refresher!: RefresherService
  @service releaseChecker!: ReleaseCheckerService
  @service session!: SessionService
  @service sessionTracker!: SessionTrackerService
  @service store!: Store
  @service teacherEvents!: TeacherEvents
  // This service has constructor that hooks into routeDidChange router events, which we want early in the app
  // eslint-disable-next-line ember/no-restricted-service-injections
  @service activeRoute!: ActiveRouteService
  @service analytics!: AnalyticsService
  @service router!: RouterService
  @service window!: WindowService

  refreshTokenTimer?: NodeJS.Timeout

  constructor(...args: never[]) {
    super(...args)

    this.analytics.setup()

    this.router.on('routeDidChange', this.checkAppRefresher)
    this.router.on('routeDidChange', ({ to }) => {
      this.teacherEvents.track(to as RouteWithKnownAttributes)
    })
  }

  override willDestroy() {
    this.router.off('routeDidChange', this.checkAppRefresher)
  }

  async beforeModel(transition: Transition) {
    // embroider macros should ensure that mirage code is tree-shaken out of production build
    if (macroCondition(isDevelopingApp() && !isTesting())) {
      await bootMirage()
    }

    // Load the base translations to use (en)
    const defaultLanguageTag = 'en'
    await this.loadTranslations(defaultLanguageTag)
    /* Set a temporary default language here, so that if an error occurs we can still show error messaging from
     * translations. For example, the application error substate page uses intl translation keys.
     * The language is set again in the afterModel once we have the disco model and know the selected language to use.
     */
    this.intl.setLocale([defaultLanguageTag])

    return this.getTokenOrRedirect().match(
      async (_token) => {
        this.releaseChecker.start()
        this.longSessionKiller.start()
        const { decodedToken } = this.authToken
        this.session.setAuthUser(decodedToken as unknown as DecodedAuthToken)
        return Promise.resolve()
      },
      (_error) => {
        // Abort the transition to ensure no further model hooks take place
        void transition.abort()
      },
    )
  }

  async model() {
    const { authUser } = this.session
    assert('authUser is not set', authUser)

    const [districtCoordinator, district] = await Promise.all([
      // reload ensures promises wait until request finishes (in case record already in cache without includes)
      this.store.findRecord<DistrictCoordinator>('district-coordinator', authUser.id, { reload: true }),
      this.store.findRecord<District>('district', authUser.districtId, {
        // @ts-expect-error - include types not correct
        include: 'standards-set',
        reload: true,
      }),
    ])
    this.session.setDistrictCoordinator(districtCoordinator)
    this.session.setDistrict(district)

    this.analytics.register({
      district_id: district.id,
    })

    return { districtCoordinator, district }
  }

  async afterModel({ districtCoordinator }: { districtCoordinator: DistrictCoordinator }) {
    const language = districtCoordinator.language || 'en-us'
    const languageTag = String(language).toLowerCase()
    await this.loadTranslations(languageTag)
    this.intl.setLocale([languageTag, 'en'])

    assert('disco id must not be null', districtCoordinator.id)

    this.sessionTracker.start(districtCoordinator.id)

    this.teacherEvents.setupTeacherTotter()

    await this.gradeSets.loadGradeSets()

    this.scheduleRefresh()
  }

  async loadTranslations(language: string) {
    // since value isn't known at buildtime (can't enforce with types), check it at runtime
    if (supportedLanguageTags.includes(language)) {
      const { default: translations } = await import(`@translations/${language}.json`)

      this.intl.addTranslations(language, translations)
    } else {
      // we don't have translations to load for this language tag - they'll end up with base translations.
      this.intl.addTranslations(language, {})
    }
  }

  scheduleRefresh() {
    if (!config.autoRefreshJWT.enabled) return

    const expiry = this.authToken.tokenExpiry

    const oneMinute = 60 * 1000

    const refreshAt = expiry - oneMinute - Date.now() // Refresh the JWT a minute before it expires

    this.refreshTokenTimer = setTimeout(() => {
      /* In case the browser went to sleep or something, do a final check to ensure we're within the expiry window
       * before attempting to refresh the token.
       */
      if (Date.now() < expiry) {
        void this.refreshToken()
      } else {
        this.redirectToLogin({ returnToCurrentLocation: true })
      }
    }, refreshAt)
  }

  async refreshToken() {
    await this.authToken
      .requestToken()
      .andThen((_token) => this.getTokenOrRedirect())
      .map((_token) => this.scheduleRefresh())
  }

  /**
   * Retreives the token. Redirects to login if this fails.
   * Instructs the redirection to avoid a return_to link if a token was found but usertype is wrong, the login page will
   * take them to the correct place.
   */
  getTokenOrRedirect() {
    return this.authToken.getTokenForDisco().mapErr((err) => {
      const incorrectUserType = err.message.includes('does not match disco')
      this.redirectToLogin({ returnToCurrentLocation: !incorrectUserType })
      return err
    })
  }

  /**
   * Send them back to the login page so they can get auth'd. If desired, set the return_to, so that when logged in,
   * they'll be back at the page they wanted.
   */
  redirectToLogin = ({ returnToCurrentLocation = false }: { returnToCurrentLocation: boolean }) => {
    const url = new URL(config.loginUrl)
    if (returnToCurrentLocation) url.searchParams.append('return_to', this.window.getWindow.location.href)
    this.window.getWindow.location.assign(url.href)
  }

  deactivate() {
    if (this.refreshTokenTimer) clearTimeout(this.refreshTokenTimer)
  }

  checkAppRefresher = () => {
    this.refresher.refreshIfScheduled()
  }

  // Route error handlers are called synchronously so cannot return a promsie
  // which also means we can't use async/await
  @action // @action needed for hook to be called.
  error(error: unknown, _transition: Transition) {
    // don't report abort errors
    if (isAbortError(error)) {
      return false
    }

    // if unauthorized or forbidden, their token would have expired - send them to login and dont report.
    if (isUnauthorizedError(error) || isForbiddenError(error)) {
      this.redirectToLogin({ returnToCurrentLocation: false })
      return false
    }

    if (error instanceof Response) {
      void error.text().then((text) => this.log.error('unknown error occurred', text))
    } else if (error) {
      this.log.error('unknown error occurred', error)
    } else {
      this.log.error('unknown error occurred')
    }

    return true
  }
}
